// Central access model for content pages. Every page has one of three levels:
//
//   'public'  — anyone, signed in or not.
//   'member'  — anyone with an account (signed in), regardless of permission.
//   'private' — signed in AND has permission for the page (admins see all).
//
// This is the single source of truth used by AuthContext.hasAccess,
// ProtectedRoute, and Home. Pages not listed default to 'private'.
//
// NOTE: for pages rendered from data files bundled into the client (the plan
// pages and the blog), these levels gate the UI only — the underlying content
// still ships in the public JS bundle. Only the API-backed pages (Voters,
// School Staff, Town Budgets, Memos) are protected server-side as well.
export const PAGE_ACCESS = {
  // Public
  '/chca-faq': 'public',
  '/outdoor-lighting-faq': 'public',
  // The Overview and FAQ tabs are open to anyone. The Submit tab on the same
  // page is NOT: it is shown only to accounts holding an explicit
  // '/faq-platform-faq' permission row, and the upload API enforces the same
  // row server-side (backend/routes/faqSubmissions.js). A page being 'public'
  // therefore does not mean every tab on it is.
  '/2026-stm2-crypto-kiosk-faq': 'public', // petitioners' FAQ, Warrant Article 7
  '/2026-stm2-reimburse-tmms-faq': 'public', // petitioner's FAQ, Warrant Article 5
  '/2026-stm2-psmod-faq': 'public', // petitioners' FAQ, Warrant Articles 15 and 16
  '/faq-platform-faq': 'public',
  '/governing-documents': 'public', // and every /governing-documents/:docId
  '/26-pleasant-st-massing-study': 'public', // zoning-envelope study, PSMOD
  '/faqs': 'public',    // directory of the FAQ pages
  '/studies': 'public', // directory of the studies; its items vary
  '/notepad': 'public', // blog list (individual articles carry their own access)

  // Private (permission-gated)
  '/dashboards': 'private', // the directory of the plan dashboards below
  '/roadmap-2030': 'private',
  '/carp-2026': 'private',
  '/urban-forestry': 'private',
  '/open-space': 'private',
  '/ersc-recommendations': 'private',
  '/vision-zero': 'private',
  '/athletic-fields': 'private',
  '/community-health-plan': 'private',
  '/dpw-strategic-plan': 'private',
  '/hazard-mitigation': 'private',
  '/housing-production-plan': 'private',
  '/town-school-moa': 'private',
  '/voters': 'private',
  '/school-staff': 'private',
  '/town-budgets': 'private',
  '/memos': 'private',
};

export const DEFAULT_ACCESS = 'private';

// Resolve the access level for a path, honoring sub-routes via longest-prefix
// match (e.g. /governing-documents/:id inherits /governing-documents). The blog
// list matches only itself here; individual articles are gated by their own
// `access` field in the component, not through this map.
export function accessForPath(pagePath) {
  if (PAGE_ACCESS[pagePath]) return PAGE_ACCESS[pagePath];
  let best = null;
  let bestLen = -1;
  for (const base of Object.keys(PAGE_ACCESS)) {
    if (base === '/notepad') continue; // don't fold articles into the public list
    if (pagePath === base || pagePath.startsWith(base + '/')) {
      if (base.length > bestLen) { best = PAGE_ACCESS[base]; bestLen = base.length; }
    }
  }
  return best || DEFAULT_ACCESS;
}
